import { on } from '@ngrx/store';

import { jwtDecode } from 'jwt-decode';

import { emptyState, errorState, loadingState, resultState } from '../../@core/fetch-state.utils';
import { User } from '../../@core/services/session-manager.service';
import { AppState } from '../app.state.interface';

import { UserApiActions } from './user.actions';
import { UserState } from './user.state.interface';

export const userStateFactory: UserState = {
  loggedInUser: emptyState(),
  activeUsers: emptyState(),
  activeUsersTotalCount: 0,
  archivedUsers: emptyState(),
  archivedUsersTotalCount: 0,
  fetchedUser: emptyState(),
  fetchedUserRoles: emptyState(),
  archivedUser: emptyState(),
  unarchivedUser: emptyState(),
  addedUser: emptyState(),
  updatedUser: emptyState(),
  changedPassword: emptyState(),
};

export const userStateReducers = [
  // Login actions
  on(UserApiActions.login, (state: AppState) => ({
    ...state,
    loggedInUser: loadingState(),
  })),

  on(UserApiActions.loginSuccess, (state: AppState, { payload }) => {
    const decodedToken = jwtDecode(payload.token) as Partial<User>;

    return {
      ...state,
      loggedInUser: resultState({
        token: payload.token,
        id: decodedToken.id,
        firstName: decodedToken.firstName,
        lastName: decodedToken.lastName,
        roles:
          typeof decodedToken.roles === 'string' ? decodedToken.roles : decodedToken?.roles?.[0],
      }),
    };
  }),

  on(UserApiActions.loginError, (state: AppState, { payload }) => ({
    ...state,
    loggedInUser: errorState([payload.message]),
  })),

  // Logout actions
  on(UserApiActions.logout, (state: AppState) => ({
    ...state,
    loggedInUser: emptyState(),
  })),

  // Load local storage actions
  on(UserApiActions.loadLocalStorageSuccess, (state: AppState, { payload }) => ({
    ...state,
    loggedInUser: resultState(payload),
  })),

  // Active users actions
  on(UserApiActions.fetchActiveUsers, (state: AppState) => ({
    ...state,
    activeUsers: loadingState(),
    activeUsersTotalCount: 0,
  })),

  on(UserApiActions.fetchActiveUsersSuccess, (state: AppState, { users, totalCount }) => ({
    ...state,
    activeUsers: resultState(users),
    activeUsersTotalCount: totalCount,
  })),

  on(UserApiActions.fetchActiveUsersError, (state: AppState, { error }) => ({
    ...state,
    activeUsers: errorState([error]),
    activeUsersTotalCount: 0,
  })),

  // Archived users actions
  on(UserApiActions.fetchArchivedUsers, (state: AppState) => ({
    ...state,
    archivedUsers: loadingState(),
    archivedUsersTotalCount: 0,
  })),

  on(UserApiActions.fetchArchivedUsersSuccess, (state: AppState, { users, totalCount }) => ({
    ...state,
    archivedUsers: resultState(users),
    archivedUsersTotalCount: totalCount,
  })),

  on(UserApiActions.fetchArchivedUsersError, (state: AppState, { error }) => ({
    ...state,
    archivedUsers: errorState([error]),
  })),

  // Archive user actions
  on(UserApiActions.archiveUser, (state: AppState) => ({
    ...state,
    archivedUser: loadingState(),
  })),

  on(UserApiActions.archiveUserSuccess, (state: AppState, { archivedUser }) => ({
    ...state,
    archivedUser: resultState(archivedUser),
  })),

  on(UserApiActions.archiveUserError, (state: AppState, { error }) => ({
    ...state,
    archivedUser: errorState([error]),
  })),

  // Unarchive user actions
  on(UserApiActions.unarchiveUser, (state: AppState) => ({
    ...state,
    unarchivedUser: loadingState(),
  })),

  on(UserApiActions.unarchiveUserSuccess, (state: AppState, { unarchivedUser }) => ({
    ...state,
    unarchivedUser: resultState(unarchivedUser),
  })),

  on(UserApiActions.unarchiveUserError, (state: AppState, { error }) => ({
    ...state,
    unarchivedUser: errorState([error]),
  })),

  // Fetch Specific User actions
  on(UserApiActions.fetchUser, (state: AppState) => ({
    ...state,
    fetchedUser: loadingState(),
  })),

  on(UserApiActions.fetchUserSuccess, (state: AppState, { user }) => ({
    ...state,
    fetchedUser: resultState(user),
  })),

  on(UserApiActions.fetchUserError, (state: AppState, { error }) => ({
    ...state,
    fetchedUser: errorState([error]),
  })),

  // Fetch User Roles actions
  on(UserApiActions.fetchUserRoles, (state: AppState) => ({
    ...state,
    fetchedUserRoles: loadingState(),
  })),

  on(UserApiActions.fetchUserRolesSuccess, (state: AppState, { roles }) => ({
    ...state,
    fetchedUserRoles: resultState(roles),
  })),

  on(UserApiActions.fetchUserRolesError, (state: AppState, { error }) => ({
    ...state,
    fetchedUserRoles: errorState([error]),
  })),

  // Add user actions
  on(UserApiActions.addUser, (state: AppState) => ({
    ...state,
    addedUser: loadingState(),
  })),

  on(UserApiActions.addUserSuccess, (state: AppState, { addedUser }) => ({
    ...state,
    addedUser: resultState(addedUser),
  })),

  on(UserApiActions.addUserError, (state: AppState, { error }) => ({
    ...state,
    addedUser: errorState([error]),
  })),

  // Update user actions
  on(UserApiActions.updateUser, (state: AppState) => ({
    ...state,
    updatedUser: loadingState(),
  })),

  on(UserApiActions.updateUserSuccess, (state: AppState, { updatedUser }) => ({
    ...state,
    updatedUser: resultState(updatedUser),
  })),

  on(UserApiActions.updateUserError, (state: AppState, { error }) => ({
    ...state,
    updatedUser: errorState([error]),
  })),

  // Change password actions
  on(UserApiActions.changePassword, (state: AppState) => ({
    ...state,
    changedPassword: loadingState(),
  })),

  on(UserApiActions.changePasswordSuccess, (state: AppState, { changedPassword }) => ({
    ...state,
    changedPassword: resultState(changedPassword),
  })),

  on(UserApiActions.changePasswordError, (state: AppState, { error }) => ({
    ...state,
    changedPassword: errorState([error]),
  })),
];
